import React, { useCallback } from 'react'
import {
  Box,
  DialogContent,
  Typography,
  useMediaQuery,
  Avatar,
  SvgIcon,
  Divider,
  Theme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { format } from 'date-fns'

import Dialog from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import HapanaSparkLoading from '@hapana/shared/web/components/shared/hapana-spark-loading/hapana-spark-loading.component'
import RichContentEditor from '@hapana/shared/web/components/shared/rich-content-editor/rich-content-editor.component'
import { useMutation } from '@hapana/shared/web/services/gqty'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import {
  FORM_FIELDS,
  FORM_DEFAULTS,
  PLACEHOLDERS,
  LOADING_SPINNER_SIZE,
  DATE_FORMAT,
  RICH_EDITOR_HEIGHT,
} from './add-note.constants'
import { styles } from './add-note.styles'

import CalendarIcon from '@hapana/shared/web/styles/assets/svgs/calendar.svg'
import { getSiteId } from '../client-notes.constants'
import { getAvatarColor } from '../../../../../utils/avatar-color.util'
import {
  type ClientPublicSchema,
  type ClientNotePublicSchema,
  type ClientNoteCreateInputSchema,
  type CreateClientNoteResponseSchema,
} from '@hapana/shared/schemas/users/client-schemas'

type FormData = {
  noteContent: string
}

type AddNoteDialogProps = {
  client: ClientPublicSchema
  onClose?: () => void
  onDataChange?: (response: ClientNotePublicSchema) => void
}

const AddNoteDialog: React.FC<AddNoteDialogProps> = ({ client, onClose, onDataChange }) => {
  const { t } = useTranslation('core')
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const { popDialog } = useDialog()
  const { openToast } = useToast()

  const siteId = getSiteId()
  const [createClientNote, { isLoading }] = useMutation(
    (
      mutation,
      args: {
        input: ClientNoteCreateInputSchema
        siteId?: string
      }
    ) => {
      const { input, siteId } = args
      const result = mutation.createClientNote({
        input,
        siteId: siteId || getSiteId(),
      })
      if (result) {
        void result.success
        void result.message
        void result.note?.noteId
        void result.note?.content
        void result.note?.createdBy
        void result.note?.createdByName
        void result.note?.createdAt
      }
      return result
    },
    {
      onComplete: (data: CreateClientNoteResponseSchema) => {
        if (data?.success) {
          openToast({
            title: t('core:clients.notes.addNote.successMessage'),
            severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
          })
          if (onDataChange && data.note) onDataChange(data.note)
          handleClose()
        } else {
          openToast({
            title: t('core:clients.notes.addNote.errorMessage'),
            severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
          })
        }
      },
      onError: () => {
        openToast({
          title: t('core:clients.notes.addNote.errorMessage'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
      },
    }
  )

  const formMethods = useForm<FormData>({
    defaultValues: FORM_DEFAULTS,
    mode: 'onChange',
  })

  const { handleSubmit } = formMethods

  const handleClose = useCallback(() => {
    if (onClose) onClose()
    popDialog()
  }, [onClose, popDialog])

  const handleSave = useCallback(
    async (formData: FormData) => {
      const noteData = {
        clientId: client.id,
        content: formData.noteContent,
      }

      createClientNote({
        args: {
          input: noteData,
          siteId,
        },
      })
    },
    [client.id, createClientNote, siteId]
  )

  const getClientDisplayName = (): string => {
    if (client.firstName && client.lastName) {
      return `${client.firstName} ${client.lastName}`
    }
    return client.name || ''
  }

  const getClientInitials = (): string => {
    const name = getClientDisplayName()
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const dialogConfig = {
    title: t('core:clients.notes.addNote.title'),
    closeType: CLOSE_TYPES.TEXT,
    closeLabel: t('common:cancel'),
    size: isMobile ? SIZES.SM : SIZES.MD,
    primaryAction: {
      label: isLoading ? (
        <Box sx={styles.loadingContainer}>
          <HapanaSparkLoading size={LOADING_SPINNER_SIZE} />
        </Box>
      ) : (
        t('core:clients.notes.addNote.save')
      ),
      action: handleSubmit(handleSave),
      disabled: isLoading,
      fullWidth: isTablet,
      theme: 'green',
    },
    onClose: isLoading ? undefined : handleClose,
  }

  return (
    <Dialog {...dialogConfig} hasMobileBottomSheet>
      <DialogContent sx={styles.dialogContent}>
        <FormProvider {...formMethods}>
          <Box sx={styles.headerContainer}>
            <Typography variant="header2">{t('core:clients.notes.addNote.newNote')}</Typography>

            <Box sx={styles.clientInfo}>
              <Avatar
                sx={{
                  ...styles.clientAvatar,
                  bgcolor: getAvatarColor(client.id),
                }}
              >
                {getClientInitials()}
              </Avatar>
              <Box>
                <Typography variant="header4" color="gray.64">
                  {getClientDisplayName()}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={styles.divider} />

          <Box sx={styles.formContainer}>
            <Box sx={styles.dateRow}>
              <SvgIcon component={CalendarIcon} sx={styles.calendarIcon} />
              <Typography variant="medium" color="gray.64">
                {format(new Date(), DATE_FORMAT)}
              </Typography>
            </Box>

            <Divider />

            <Box sx={styles.formRow}>
              <Box sx={styles.contentEditor}>
                <RichContentEditor
                  name={FORM_FIELDS.NOTE_CONTENT}
                  height={RICH_EDITOR_HEIGHT}
                  placeholder={PLACEHOLDERS.NOTE_CONTENT}
                />
              </Box>
            </Box>
          </Box>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

export default AddNoteDialog
